/**
 * Nonsole Readiness - Zod schemas + inferred TypeScript types
 *
 * Single source of truth for the report contract across the three surfaces
 * that touch it:
 *
 *   - apps/node-service       (soft-validates the PlayJector reply, POSTs to realtime-api)
 *   - apps/realtime-api       (hard-validates the POST, persists, serves admin queries)
 *   - apps/admin-dashboard    (renders the report, derives summaries, filters fleet)
 *
 * These mirror the Phase 1 JSON contract (`schemaVersion: 2`) emitted by the
 * C++ PlaycastService and forwarded by node-service. Any drift between this
 * file and the C++ serialization
 * (apps/playjector/src/PlaycastService/NonsoleReadiness/NonsoleReadinessReport.cpp)
 * is a Phase 6 review concern.
 *
 * Important contract notes:
 *
 *   - `schemaVersion` is a frozen literal (`2`). Bumping it is a coordinated
 *     change across C++ + Zod + Mongo + dashboard.
 *   - `gates[].statusLastChangedAtUtc` arrives as `null` from the C++ side on
 *     every report. The realtime-api populates it server-side in the
 *     `computeStatusChangeTimestamps` step before persisting. The Zod schema
 *     therefore accepts `string | null` so it can parse both shapes
 *     (incoming-from-C++ null, and stored-in-Mongo populated string) with the
 *     same validator.
 *   - `reportHash` is REQUIRED non-empty. C++ skips publishing if `Finalize()`
 *     fails so a null/empty hash cannot legitimately reach the wire; the soft
 *     validator on the node-service side rejects any reply that violates this,
 *     and realtime-api's Zod parse rejects again at the boundary.
 *   - `evaluatedAtUtc` is REQUIRED non-empty (used as the stable submission
 *     identity for the coordinator's out-of-order completion guard).
 *   - Numeric "ms" fields (`retryHintMs`, `evaluationCostMs`) are nullable
 *     per the Phase 1 spec (`0 -> null` in C++).
 *   - Strings that are semantically optional (`reasonCode`, `reasonMessage`,
 *     `nextEligibleAtUtc`, `idleStatus.lastActivityAtUtc`,
 *     `idleStatus.projectedEligibleAtUtc`) arrive as `null` rather than
 *     missing keys.
 */

import { z } from 'zod';
import { hostHealthSnapshotSchema } from '@playcastdotio/Messaging';

// ============================================================================
// Enumerations
// ============================================================================

export const gateCategoryValues = [
  'prerequisites',
  'environment',
  'health',
  'entry-conditions',
] as const;
export type GateCategory = (typeof gateCategoryValues)[number];

export const gateSeverityValues = ['blocking', 'warning', 'info'] as const;
export type GateSeverity = (typeof gateSeverityValues)[number];

export const evaluationModeValues = ['periodic', 'entry-attempt'] as const;
export type EvaluationMode = (typeof evaluationModeValues)[number];

export const gateEvaluationStatusValues = ['evaluated', 'deferred'] as const;
export type GateEvaluationStatus = (typeof gateEvaluationStatusValues)[number];

export const gateRemediationValues = ['none', 'wait', 'reboot', 'support'] as const;
export type GateRemediation = (typeof gateRemediationValues)[number];

// ============================================================================
// Sub-schemas
// ============================================================================

export const gateStateSchema = z.object({
  id: z.string().min(1),
  displayName: z.string(),
  category: z.enum(gateCategoryValues),
  severity: z.enum(gateSeverityValues),
  passed: z.boolean(),
  reasonCode: z.string().nullable(),
  reasonMessage: z.string().nullable(),
  retryHintMs: z.number().int().nonnegative().nullable(),
  evaluationCostMs: z.number().int().nonnegative().nullable(),
  evaluatedAtUtc: z.string().datetime(),
  adminDisabled: z.boolean().optional(),
  // Additive schema-v2 fields. Absence preserves the legacy "evaluated"
  // behavior; consumers must not treat a deferred dependent gate as an
  // independent entry blocker.
  evaluationStatus: z.enum(gateEvaluationStatusValues).optional(),
  deferredByGateIds: z.array(z.string().min(1)).min(1).optional(),
  remediation: z.enum(gateRemediationValues).optional(),
  // Always null on the wire from C++. Populated by realtime-api before persist.
  statusLastChangedAtUtc: z.string().nullable(),
}).superRefine((gate, context) => {
  const isDeferred = gate.evaluationStatus === 'deferred';
  if (isDeferred && gate.deferredByGateIds === undefined) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['deferredByGateIds'],
      message: 'deferred gates must identify at least one prerequisite gate',
    });
  }
  if (!isDeferred && gate.deferredByGateIds !== undefined) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['deferredByGateIds'],
      message: 'only deferred gates may identify prerequisite gates',
    });
  }
});
export type GateState = z.infer<typeof gateStateSchema>;

export const idleStatusSchema = z.object({
  idleThresholdMs: z.number().int().nonnegative(),
  lastActivityAtUtc: z.string().nullable(),
  currentIdleMs: z.number().int().nonnegative(),
  idlePercent: z.number().int().min(0).max(100),
  projectedEligibleAtUtc: z.string().nullable(),
});
export type IdleStatus = z.infer<typeof idleStatusSchema>;

export const idleBlockerAppHoldSchema = z.object({
  processName: z.string(),
  processId: z.number().int().nonnegative(),
  reason: z.string().nullable(),
  displayRequired: z.boolean(),
  systemRequired: z.boolean(),
  executionRequired: z.boolean().optional().default(false),
  // Fourth category: this process owns a fullscreen window. Optional + default
  // so older host reports (before the fullscreen hold existed) still parse.
  fullscreenRequired: z.boolean().optional().default(false),
  ignoredForPresence: z.boolean(),
});
export type IdleBlockerAppHold = z.infer<typeof idleBlockerAppHoldSchema>;

export const idleBlockerDriverHoldSchema = z.object({
  caller: z.string(),
  displayRequired: z.boolean(),
  systemRequired: z.boolean(),
  executionRequired: z.boolean().optional().default(false),
  awayMode: z.boolean(),
});
export type IdleBlockerDriverHold = z.infer<typeof idleBlockerDriverHoldSchema>;

export const idleBlockersSchema = z.object({
  keptAwakeByUserInput: z.boolean(),
  // Four distinct app-hold categories, mirroring the four idle-exclusion bools
  // (never welded): DisplayRequired, SystemRequired, ExecutionRequired, Fullscreen.
  keptAwakeByAppHold: z.boolean(),
  keptAwakeBySystemHold: z.boolean().optional().default(false),
  keptAwakeByExecutionHold: z.boolean().optional().default(false),
  keptAwakeByFullscreenHold: z.boolean().optional().default(false),
  lastInputAtUtc: z.string().nullable(),
  secondsSinceLastInput: z.number().int().nonnegative().nullable(),
  ignoredAppHoldCount: z.number().int().nonnegative(),
  appHolds: z.array(idleBlockerAppHoldSchema),
  driverHolds: z.array(idleBlockerDriverHoldSchema),
});
export type IdleBlockers = z.infer<typeof idleBlockersSchema>;

export const userSessionStateSchema = z.object({
  sessionId: z.number().int().nonnegative(),
  userName: z.string(),
  accountSid: z.string(),
  connectionState: z.enum([
    'active', 'connected', 'connect-query', 'shadow', 'disconnected',
    'idle', 'listen', 'reset', 'down', 'initializing', 'unknown',
  ]),
  lockState: z.enum(['Locked', 'Unlocked', 'Unknown']),
  playcastGuest: z.boolean(),
});
export type UserSessionState = z.infer<typeof userSessionStateSchema>;

// ============================================================================
// Top-level report schema (schemaVersion 2)
// ============================================================================

export const nonsoleReadinessReportSchema = z.object({
  machineId: z.string().min(1),
  schemaVersion: z.literal(2),
  evaluatedAtUtc: z.string().datetime(),
  evaluationMode: z.enum(evaluationModeValues),
  playcastServiceVersion: z.string(),

  allGatesPass: z.boolean(),
  blockingGateIds: z.array(z.string()),
  suggestedBackoffMs: z.number().int().nonnegative(),
  reportHash: z.string().min(1),
  isDeveloperMode: z.boolean(),
  nextEligibleAtUtc: z.string().nullable(),

  idleStatus: idleStatusSchema,
  idleBlockers: idleBlockersSchema.nullish(),
  // Additive schema-v2 diagnostics. Older hosts omit this during rollout.
  userSessions: z.array(userSessionStateSchema).optional(),
  gates: z.array(gateStateSchema),

  /** True when the NRM worker thread is still running (Thread::IsRunning). */
  nrmThreadAlive: z.boolean().optional(),
  /** Monotonic heartbeat updated each NRM loop tick (~30s); stale => hung. */
  nrmLastBeatAtUtc: z.string().datetime().optional(),
  /** PlaycastService process creation time (ISO-8601 UTC). */
  serviceStartedAtUtc: z.string().datetime().optional(),
});
export type NonsoleReadinessReport = z.infer<typeof nonsoleReadinessReportSchema>;

// ============================================================================
// Fleet summary response shape (GET /realtime/admin/nonsole-readiness)
// ============================================================================
//
// Totals overlap (intentional): `readyMachines`, `blockedMachines`, and
// `staleMachines` are NOT mutually exclusive partitions of `totalMachines`.
// A machine whose last report failed gates AND is older than `staleMinutes`
// counts in BOTH `blockedMachines` and `staleMachines`. The dashboard
// surfaces them as independent indicators; callers MUST NOT sum them.

export const fleetBlockingGateRowSchema = z.object({
  gateId: z.string(),
  displayName: z.string(),
  count: z.number().int().nonnegative(),
});
export type FleetBlockingGateRow = z.infer<typeof fleetBlockingGateRowSchema>;

export const fleetBlockingReasonRowSchema = z.object({
  gateId: z.string(),
  reasonCode: z.string(),
  count: z.number().int().nonnegative(),
});
export type FleetBlockingReasonRow = z.infer<typeof fleetBlockingReasonRowSchema>;

export const fleetReadinessSummarySchema = z.object({
  totalMachines: z.number().int().nonnegative(),
  readyMachines: z.number().int().nonnegative(),
  blockedMachines: z.number().int().nonnegative(),
  staleMachines: z.number().int().nonnegative(),
  blockingGates: z.array(fleetBlockingGateRowSchema),
  blockingReasons: z.array(fleetBlockingReasonRowSchema),
});
export type FleetReadinessSummary = z.infer<typeof fleetReadinessSummarySchema>;

// ============================================================================
// Fleet per-host fingerprint response shape
// (GET /realtime/admin/nonsole-readiness/fingerprints)
// ============================================================================

/**
 * Compact per-host readiness summary used by list views (OnlineHosts table,
 * FleetView table). Returned as one entry per machine that has *ever*
 * reported; never-reported machines are omitted (the dashboard treats
 * absence as "unknown" via the existing `nonsoleReadiness === undefined`
 * convention in `onlineHostsFilterSort.ts`).
 *
 * Stale rendering convention (the cell renders these distinctly):
 *
 *   - `isStale && allGatesPass`   -> "Last: Ready . 23m ago"
 *   - `isStale && !allGatesPass`  -> "Last: Blocked by <gate> . 23m ago"
 *   - `!isStale && allGatesPass`  -> "Ready"
 *   - `!isStale && !allGatesPass` -> "Blocked by <gate>"
 *
 * The cell label is derived; only the raw fields below ship over the wire.
 */
export const fleetBlockingReasonPairSchema = z.object({
  gateId: z.string(),
  reasonCode: z.string(),
});
export type FleetBlockingReasonPair = z.infer<typeof fleetBlockingReasonPairSchema>;

export const fleetReadinessFingerprintSchema = z.object({
  machineId: z.string(),
  allGatesPass: z.boolean(),
  blockingGateIds: z.array(z.string()),
  blockingReasons: z.array(fleetBlockingReasonPairSchema),
  evaluatedAtUtc: z.string(),
  isStale: z.boolean(),
  nrmThreadAlive: z.boolean().optional(),
  nrmLastBeatAtUtc: z.string().optional(),
  serviceStartedAtUtc: z.string().optional(),
});
export type FleetReadinessFingerprint = z.infer<typeof fleetReadinessFingerprintSchema>;

export const fleetReadinessFingerprintsResponseSchema = z.object({
  fingerprints: z.array(fleetReadinessFingerprintSchema),
  computedAtUtc: z.string(),
  staleMinutes: z.number().int().positive(),
});
export type FleetReadinessFingerprintsResponse = z.infer<
  typeof fleetReadinessFingerprintsResponseSchema
>;

// ============================================================================
// Per-machine GET response (GET /realtime/admin/nonsole-readiness/:machineId)
// ============================================================================

export const machineNonsoleReadinessResponseSchema = z.object({
  report: nonsoleReadinessReportSchema,
  updatedAt: z.string(),
});
export type MachineNonsoleReadinessResponse = z.infer<
  typeof machineNonsoleReadinessResponseSchema
>;

// ============================================================================
// node-service heartbeat — PlaycastService SCM + PlayJector IPC status
// ============================================================================
//
// Reported every 5 minutes on POST /realtime/auth/heartbeat. Persisted on the
// machine document by realtime-api. playcastServiceLastRunningAt is derived
// server-side when playcastServiceRunning=true; playjectorUnreachableMs is only
// meaningful (and persisted) while playjectorConnected=false.
//
// All fields optional so older clients that omit them still validate. Unknown
// keys on the wider heartbeat body are stripped, not rejected (default zod
// behavior), so this can validate the full req.body without over-rejecting.

export const nodeServiceHeartbeatServiceStatusSchema = z.object({
  playcastServiceRunning: z.boolean().optional(),
  playcastServiceState: z.string().optional(),
  playjectorConnected: z.boolean().optional(),
  // Milliseconds since PlayJector IPC was last reachable. Reject NaN/Infinity
  // (`.finite()`) and negatives (`.nonnegative()`); absent is allowed.
  playjectorUnreachableMs: z.number().finite().nonnegative().optional(),
  localRuntimeHealth: hostHealthSnapshotSchema.optional(),
});
export type NodeServiceHeartbeatServiceStatus = z.infer<
  typeof nodeServiceHeartbeatServiceStatusSchema
>;
